import React from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { NUKI_APPS_BASE_INFORMATION } from '@/utils/constants';

interface AppStoreListProps {
  userAgent: string;
  deviceVendor?: string;
}

export default function AppStoreList({
  userAgent = 'other',
  deviceVendor,
}: Readonly<AppStoreListProps>) {
  const t = useTranslations('LogoBar');

  const { iosDownloadMetadata, androidDownloadMetadata, huaweiDownloadMetadata } =
    NUKI_APPS_BASE_INFORMATION;

  const isIos = /iOS|iPhone OS|iPadOS/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const isMobile = isIos || isAndroid;
  const isHuaweiDevice = /huawei|honor/i.test(deviceVendor ?? '');

  // Huawei AppGallery is only relevant on desktop (where the device is unknown)
  // or on actual Huawei/Honor devices, which ship without Google Play services.
  const shouldShowHuawei = !isMobile || isHuaweiDevice;

  return (
    <div className="flex flex-row gap-4">
      {!isAndroid && (
        <Link
          href={iosDownloadMetadata.href}
          target="_blank"
          rel="noreferrer"
          aria-label={t('appStoreDownload')}
        >
          <OptimizedImage
            image={iosDownloadMetadata.image}
            width={150}
            height={50}
            className=" h-12 w-auto"
            sizes="300px"
          />
        </Link>
      )}

      {!isIos && (
        <Link
          href={androidDownloadMetadata.href}
          target="_blank"
          rel="noreferrer"
          aria-label={t('googlePlayDownload')}
        >
          <OptimizedImage
            image={androidDownloadMetadata.image}
            width={150}
            height={50}
            className=" h-12 w-auto"
            sizes="300px"
          />
        </Link>
      )}

      {shouldShowHuawei && (
        <Link
          href={huaweiDownloadMetadata.href}
          target="_blank"
          rel="noreferrer"
          aria-label={t('huaweiAppGalleryDownload')}
        >
          <OptimizedImage
            image={huaweiDownloadMetadata.image}
            width={150}
            height={50}
            className=" h-12 w-auto"
            sizes="300px"
          />
        </Link>
      )}
    </div>
  );
}
